<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" placement="bottom-end" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton icon="person" round preset="secondary" color="textPrimary" class="profile-dropdown__button" />
      </template>
      <VaDropdownContent class="profile-dropdown__content px-0 py-4" :style="{ '--hover-color': hoverColor }">
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ getTranslation(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleItemClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ getTranslation(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>

        <!-- Административный раздел для суперпользователей -->
        <VaList v-if="isSuperuser">
          <VaListSeparator class="mx-3 my-2" />
          <header class="admin-section-header">
            <VaIcon name="security" size="14px" class="mr-1" />
            {{ getTranslation('user.system') }}
          </header>
          <VaListItem class="menu-item px-4 text-base cursor-pointer h-10 admin-menu-item" @click="handleAdminAccess">
            <VaIcon name="admin_panel_settings" class="pr-1" color="danger" />
            {{ getTranslation('user.administration') }}
            <VaBadge color="danger" text="SU" size="small" class="ml-auto admin-badge" />
          </VaListItem>
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useAuthStore } from '../../../../stores/auth'
import { useRouter } from 'vue-router'
import { useAdminAccess } from '../../../../composables/useAdminAccess'
import { ProtectedRoute } from '../../../../config/permissions'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))
const authStore = useAuthStore()
const router = useRouter()
const { requestAdminAccess } = useAdminAccess()

// Вычисляемое свойство для проверки суперпользователя
const isSuperuser = computed(() => authStore.user?.is_superuser === true)

const { t } = useI18n()

// Безопасный метод получения переводов
const getTranslation = (key: string): string => {
  try {
    const translation = t(key)
    return typeof translation === 'string' ? translation : key
  } catch (error) {
    console.warn(`Translation key issue: ${key}`, error)
    return key
  }
}

type ProfileListItem = {
  name: string
  to?: string | { name: string }
  href?: string
  icon: string
  action?: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: { name: ProtectedRoute.PREFERENCES },
            icon: 'mso-account_circle',
          },
          {
            name: 'settings',
            to: { name: ProtectedRoute.SETTINGS },
            icon: 'mso-settings',
          },
          {
            name: 'projects',
            to: { name: ProtectedRoute.PROJECTS },
            icon: 'mso-favorite',
          },
        ],
      },
      {
        name: 'explore',
        separator: true,
        list: [
          {
            name: 'faq',
            to: { name: ProtectedRoute.FAQ },
            icon: 'mso-quiz',
          },
          {
            name: 'helpAndSupport',
            href: 'mailto:api-support@udacapi.com',
            icon: 'mso-error',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            action: 'logout',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.action) {
    return {}
  }
  if (item.to) {
    // If to is already an object, use it directly; otherwise wrap it in { name: ... }
    return { to: typeof item.to === 'string' ? { name: item.to } : item.to }
  }
  return item.href ? { href: item.href, target: '_blank' } : {}
}

const handleItemClick = (item: ProfileListItem) => {
  if (item.name === 'logout') {
    isShown.value = false
    authStore.logout()
  }
}

// Новый метод для обработки доступа к админ панели
const handleAdminAccess = async () => {
  try {
    await requestAdminAccess()
    // Закрываем dropdown после успешного доступа
    isShown.value = false
    // Переходим в админ панель
    router.push({ name: 'admin-dashboard' })
  } catch (error) {
    console.error('Admin access request failed:', error)
    // Можно добавить уведомление об ошибке здесь
  }
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  .va-dropdown__anchor {
    display: inline-block;
  }

  &__content {
    width: 15rem;
    min-width: 15rem;
    max-width: calc(100vw - 2rem);

    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__button {
    width: 36px;
    min-width: 36px;
    height: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__button .va-button__content {
    width: 100%;
    height: 100%;
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}

/* Стили для административного раздела */
.admin-section-header {
  display: flex;
  align-items: center;
  color: #ff4757;
  font-weight: bold;
  font-size: 11px;
  text-transform: uppercase;
  opacity: 0.9;
  padding: 8px 16px 4px 16px;
  margin-bottom: 4px;
}

.admin-menu-item {
  background: linear-gradient(45deg, rgba(255, 71, 87, 0.05), rgba(255, 55, 66, 0.02));
  border-left: 3px solid #ff4757;
  transition: all 0.3s ease;
}

.admin-menu-item:hover {
  background: linear-gradient(45deg, rgba(255, 71, 87, 0.15), rgba(255, 55, 66, 0.08));
}

.admin-badge {
  animation: adminPulse 2s ease-in-out infinite;
}

@keyframes adminPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
